import type { ReactNode } from 'react'
import { cn } from '@/shared/lib/cn'
import s from './PageHeader.module.css'

type PageHeaderProps = {
  /**
   * Маркер-категория над заголовком. НЕОБЯЗАТЕЛЕН: у «Гостевого пути» его в макете нет вовсе
   * (строка 649 — заголовок идёт первым элементом), хотя у остальных разделов Студии он есть.
   */
  eyebrow?: ReactNode
  title: string
  description: string
  /**
   * Размер заголовка — НА ТЕХ ЭКРАНАХ, КОТОРЫЕ РИСУЕТ ЭТОТ КОМПОНЕНТ (макет
   * `prototype/Prototype.dc.html`):
   *
   *   `lg` — 34px: Брони и библиотека текстов. Сводка в макете тоже 34px, но через этот компонент
   *          не идёт: свой `h1` она рисует в `DashboardSummary`.
   *   `md` — 32px: разделы Студии, идущие через `StudioSectionFrame` — Платежи, Источники, Аудит,
   *          Бренд.
   *   `sm` — 29px: «Гостевой путь».
   *
   * ⚠️ У «Гостей» соответствия в макете НЕТ, и это осознанно: там мастер-детейл, единственный `h1` —
   * 27px у ВЫБРАННОЙ карточки, заголовка раздела нет вовсе. Свою шапку этот раздел рисует сам
   * (`ClientsScreen`), как и Квартиры.
   *
   * ⚠️ ЭТО НЕ ПЕРЕЧЕНЬ ВСЕХ FRAUNCES-ЗАГОЛОВКОВ МАКЕТА: там есть ещё 28px и 27px, но принадлежат
   * они ВЫБРАННОЙ КАРТОЧКЕ двухпанельного экрана, а не разделу. Считать по потребителям компонента,
   * а не по вхождениям шрифта.
   */
  size?: 'lg' | 'md' | 'sm'
  className?: string
}

/**
 * Шапка раздела: маркер-категория + заголовок Fraunces + описание. Рисунок — из макета
 * (`prototype/Prototype.dc.html`), одинаковый везде, кроме размера заголовка (см. `size`).
 *
 * Вынесена в `shared/ui`, а не повторена в каждой странице: разметка и типографика совпадают
 * дословно всюду, где встречаются.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  size = 'md',
  className,
}: PageHeaderProps) {
  return (
    <div className={cn(s.root, className)} data-size={size}>
      {/* `== null`, а не проверка на истинность: `ReactNode` включает `0` и `''` — они ложны, но
          отрисовываемы, и счётчик «0» молча потерял бы маркер целиком. */}
      {eyebrow == null ? null : (
        <div className={s.eyebrow}>
          <span className={s.dot} aria-hidden="true" />
          {eyebrow}
        </div>
      )}
      <h1 className={cn(s.title, s[size])}>{title}</h1>
      <p className={s.description}>{description}</p>
    </div>
  )
}
